import type { ReactNode } from "react";
import { AlarmClock, ChevronRight, PanelLeft, Plus, Search, Settings } from "lucide-react";
import type { ShellProps, SidebarAreaNode, SidebarProjectNode, SidebarSpaceNode } from "../types";
import { ProjectGlyph, DeskMark } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/misc";
import { formatInstant, localZone } from "@/lib/dates";
import { cn, modKey } from "@/lib/utils";
import { ContainerGlyph } from "@/components/container-icon";

type Slots = ShellProps["slots"];

const row =
  "group/item relative flex h-[30px] items-center gap-2.5 rounded-md pl-2 pr-1 text-[13.5px] text-sidebar-foreground outline-none transition-colors duration-100 hover:bg-sidebar-accent has-[[aria-haspopup=menu][aria-expanded=true]]:bg-sidebar-accent";
const activeRow = "bg-sidebar-accent font-medium text-foreground";
const linkCls = "flex min-w-0 flex-1 items-center gap-2.5 self-stretch rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/40";

/** "Tomorrow 9:00 AM" → "Tomorrow 9 AM"; drops the weekday from far dates to fit the sidebar. */
function compactWake(iso: string, tz: string) {
  return formatInstant(iso, tz)
    .replace(/:00(?= [AP]M)/, "")
    .replace(/^[A-Z][a-z]{2}, /, "");
}

/**
 * T3 sidebar v2 behavior: the right edge shows a quiet count (or wake time); on hover or keyboard
 * focus it swaps for Settle + Snooze. An open menu keeps the actions visible.
 */
function Trailing({ meta, actions }: { meta: ReactNode; actions: ReactNode }) {
  return (
    <>
      {meta !== null && meta !== undefined && (
        <span className="ml-1 shrink-0 whitespace-nowrap pr-1.5 text-[12px] text-subtle tabular group-focus-within/item:hidden group-hover/item:hidden group-has-[[aria-haspopup=menu][aria-expanded=true]]/item:hidden">
          {meta}
        </span>
      )}
      <span data-focus-node-actions className="ml-1 hidden shrink-0 items-center group-focus-within/item:flex group-hover/item:flex has-[[aria-haspopup=menu][aria-expanded=true]]:flex">
        {actions}
      </span>
    </>
  );
}

/** Rounded-square initial for spaces; doubles as the expand/collapse control when it has projects. */
function SpaceToggle({ node }: { node: SidebarSpaceNode }) {
  const glyph = (
    <ContainerGlyph
      of={node.space}
      className="size-4"
      fallback={
        <span
          aria-hidden
          className="inline-flex size-4 items-center justify-center rounded-[5px] bg-foreground/[0.08] text-[9.5px] font-semibold uppercase text-foreground/70"
        >
          {node.space.name.trim().charAt(0)}
        </span>
      }
    />
  );
  if (node.projects.length === 0) return <span className="inline-flex size-4 shrink-0">{glyph}</span>;
  return (
    <button
      type="button"
      onClick={node.onToggle}
      aria-label={node.expanded ? `Collapse ${node.space.name}` : `Expand ${node.space.name}`}
      aria-expanded={node.expanded}
      className="group/tg relative inline-flex size-4 shrink-0 items-center justify-center rounded-[5px] outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
    >
      <span className="group-hover/item:opacity-0 group-focus-visible/tg:opacity-0">{glyph}</span>
      <ChevronRight
        aria-hidden
        className={cn(
          "absolute size-3.5 text-foreground/60 opacity-0 transition-transform duration-150 group-hover/item:opacity-100 group-focus-visible/tg:opacity-100",
          node.expanded && "rotate-90",
        )}
      />
    </button>
  );
}

function ProjectRow({ node, slots }: { node: SidebarProjectNode; slots: Slots }) {
  return (
    <div className={cn(row, "pl-[34px]", node.active && activeRow)}>
      <a href={node.href} className={linkCls} aria-current={node.active ? "page" : undefined}>
        <ProjectGlyph state={node.project.state} progress={node.progress} className="text-foreground/50" />
        <span className="truncate">{node.project.name}</span>
      </a>
      <Trailing meta={node.openCount ? node.openCount : null} actions={slots.containerActions(node)} />
    </div>
  );
}

function SpaceRow({ node, slots }: { node: SidebarSpaceNode; slots: Slots }) {
  return (
    <div>
      <div className={cn(row, node.active && activeRow)}>
        <SpaceToggle node={node} />
        <a href={node.href} className={linkCls} aria-current={node.active ? "page" : undefined}>
          <span className="truncate">{node.space.name}</span>
        </a>
        <Trailing meta={node.openCount ? node.openCount : null} actions={slots.containerActions(node)} />
      </div>
      {node.expanded && node.projects.length > 0 && (
        <div className="mt-px flex flex-col gap-px">
          {node.projects.map((p) => (
            <ProjectRow key={p.project.id} node={p} slots={slots} />
          ))}
        </div>
      )}
    </div>
  );
}

function AreaBlock({ node, slots }: { node: SidebarAreaNode; slots: Slots }) {
  return (
    <div className="mt-5">
      <div className={cn("group/item flex h-7 items-center gap-1 rounded-md pl-2 pr-1 has-[[aria-haspopup=menu][aria-expanded=true]]:bg-sidebar-accent", node.active && "bg-sidebar-accent")}>
        <a
          href={node.href}
          aria-current={node.active ? "page" : undefined}
          className="min-w-0 flex-1 truncate rounded text-[12.5px] font-semibold text-foreground/55 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          {node.area.name}
        </a>
        <span className="flex items-center opacity-0 transition-opacity focus-within:opacity-100 group-hover/item:opacity-100 has-[[aria-haspopup=menu][aria-expanded=true]]:opacity-100">
          {slots.areaActions(node)}
        </span>
        <button
          type="button"
          onClick={node.onToggle}
          aria-label={node.expanded ? `Collapse ${node.area.name}` : `Expand ${node.area.name}`}
          aria-expanded={node.expanded}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded text-subtle opacity-0 hover:bg-sidebar-accent hover:text-foreground focus-visible:opacity-100 group-hover/item:opacity-100"
        >
          <ChevronRight className={cn("size-3.5 transition-transform duration-150", node.expanded && "rotate-90")} />
        </button>
      </div>
      {node.expanded && (
        <div className="mt-0.5 flex flex-col gap-px">
          {node.spaces.map((s) => (
            <SpaceRow key={s.space.id} node={s} slots={slots} />
          ))}
        </div>
      )}
    </div>
  );
}

function SettledSection({ sidebar, slots }: { sidebar: ShellProps["sidebar"]; slots: Slots }) {
  const tz = localZone();
  if (sidebar.settled.length === 0) return null;
  return (
    <div className="mt-auto pt-6">
      <button
        type="button"
        onClick={sidebar.onToggleSettled}
        aria-expanded={sidebar.settledExpanded}
        className="flex h-7 w-full items-center gap-1.5 rounded-md pl-2 pr-2.5 text-[12.5px] font-semibold text-foreground/55 outline-none hover:bg-sidebar-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <ChevronRight className={cn("size-3.5 transition-transform duration-150", sidebar.settledExpanded && "rotate-90")} />
        Settled
        <span className="ml-auto font-normal text-subtle tabular">{sidebar.settled.length}</span>
      </button>
      {sidebar.settledExpanded && (
        <div className="mt-0.5 flex flex-col gap-px">
          {sidebar.settled.map((n) => {
            const name = n.kind === "space" ? n.space.name : n.project.name;
            return (
              <div key={n.kind === "space" ? n.space.id : n.project.id} className={cn(row, n.active && activeRow)}>
                <a href={n.href} className={cn(linkCls, "text-foreground/55")} aria-current={n.active ? "page" : undefined}>
                  {n.kind === "space" ? (
                    <span aria-hidden className="inline-flex size-4 shrink-0 items-center justify-center rounded-[5px] bg-foreground/[0.06] text-[9.5px] font-semibold uppercase text-foreground/50">
                      {name.trim().charAt(0)}
                    </span>
                  ) : (
                    <ProjectGlyph state={n.project.state} progress={n.progress} className="text-foreground/40" />
                  )}
                  <span className="truncate">{name}</span>
                </a>
                <Trailing
                  meta={
                    n.snoozedUntil ? (
                      <span className="inline-flex items-center gap-1 text-[11.5px]" title={`Returns ${formatInstant(n.snoozedUntil, tz)}`}>
                        <AlarmClock className="size-3" aria-hidden />
                        <span className="sr-only">Returns </span>
                        {compactWake(n.snoozedUntil, tz)}
                      </span>
                    ) : null
                  }
                  actions={slots.containerActions(n)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function FocusShell({ sidebar, slots, sidebarCollapsed, onToggleSidebar, detailPanel, children }: ShellProps) {
  return (
    <div className="flex h-full w-full overflow-hidden bg-sidebar">
      {!sidebarCollapsed && (
        <aside aria-label="Sidebar" className="flex w-[260px] shrink-0 flex-col text-sidebar-foreground">
          <div className="desk-drag desk-titlebar flex h-12 shrink-0 items-center gap-1 pl-4 pr-2">
            <span className="flex items-center gap-1.5 font-display text-[16px] font-bold tracking-[-0.02em] text-foreground">
              <DeskMark className="size-[18px]" />
              Desk
            </span>
            <div className="desk-no-drag ml-auto flex items-center">
              <Tooltip content="Search" shortcut={`${modKey}K`}>
                <Button size="icon" variant="quiet" aria-label="Search" onClick={slots.onSearch}>
                  <Search />
                </Button>
              </Tooltip>
              <Tooltip content="New task" shortcut={`${modKey}N`}>
                <Button size="icon" variant="quiet" aria-label="New task" onClick={slots.onQuickAdd}>
                  <Plus />
                </Button>
              </Tooltip>
              <Tooltip content="Hide sidebar">
                <Button size="icon" variant="quiet" aria-label="Hide sidebar" onClick={onToggleSidebar}>
                  <PanelLeft />
                </Button>
              </Tooltip>
            </div>
          </div>
          <nav aria-label="Views" className="flex flex-1 flex-col overflow-y-auto px-2 pb-2">
            <ul className="flex flex-col gap-px">
              {sidebar.nav.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.href}
                    aria-current={item.active ? "page" : undefined}
                    className={cn(row, "pr-2.5 focus-visible:ring-2 focus-visible:ring-ring/40", item.active && activeRow)}
                  >
                    <item.icon
                      aria-hidden
                      strokeWidth={1.8}
                      className={cn(
                        "size-4 shrink-0",
                        item.id === "today" ? "fill-[var(--star)] text-[var(--star)]" : item.active ? "text-foreground" : "text-foreground/50",
                      )}
                    />
                    <span className="truncate">{item.label}</span>
                    {item.count !== undefined && item.count > 0 && (
                      <span
                        className={cn(
                          "ml-auto text-[12px] tabular",
                          item.id === "review" ? "rounded-full bg-foreground/[0.09] px-1.5 font-medium leading-[18px] text-foreground/80" : "text-subtle",
                        )}
                      >
                        {item.count}
                      </span>
                    )}
                  </a>
                </li>
              ))}
            </ul>

            {sidebar.areas.map((a) => (
              <AreaBlock key={a.area.id} node={a} slots={slots} />
            ))}

            <div className="mt-3">{slots.newArea}</div>

            <SettledSection sidebar={sidebar} slots={slots} />
          </nav>
          <div className="flex h-11 shrink-0 items-center gap-1 px-2">
            <div className="min-w-0 flex-1">{slots.syncStatus}</div>
            <Tooltip content="Settings" shortcut={`${modKey},`}>
              <a
                href={slots.settingsHref}
                aria-label="Settings"
                aria-current={slots.settingsActive ? "page" : undefined}
                className={cn(
                  "inline-flex size-7 items-center justify-center rounded-md text-foreground/55 outline-none hover:bg-sidebar-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40",
                  slots.settingsActive && "bg-sidebar-accent text-foreground",
                )}
              >
                <Settings className="size-4" />
              </a>
            </Tooltip>
          </div>
        </aside>
      )}
      <main
        className={cn(
          "relative my-2 mr-2 flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border/80 bg-background shadow-[0_1px_2px_rgba(37,36,34,0.04)] dark:border-white/[0.06] dark:shadow-none",
          sidebarCollapsed && "ml-2",
        )}
      >
        <div aria-hidden className="desk-drag absolute inset-x-0 top-0 hidden h-5 [html.is-electron_&]:block" />
        {sidebarCollapsed && (
          <div className="desk-drag absolute left-2 top-2 z-10">
            <Tooltip content="Show sidebar">
              <Button size="icon" variant="quiet" aria-label="Show sidebar" onClick={onToggleSidebar} className="desk-no-drag">
                <PanelLeft />
              </Button>
            </Tooltip>
          </div>
        )}
        <div id="desk-main-scroll" className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[780px] px-6 pb-16 pt-10 has-[[data-wide]]:max-w-none sm:px-10">{children}</div>
        </div>
        <div className="flex h-10 shrink-0 items-center justify-end border-t border-border/60 px-2.5">{slots.toolbar}</div>
      </main>
      {detailPanel && <aside className="my-2 mr-2 w-[420px] shrink-0 overflow-hidden rounded-xl border border-border bg-card">{detailPanel}</aside>}
    </div>
  );
}
